import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'lg' | 'md';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Renders a spinner and disables the button. */
  loading?: boolean;
  fullWidth?: boolean;
  children: ReactNode;
}

const variants: Record<Variant, string> = {
  primary: 'bg-accent text-on-accent hoverable active:bg-accent-hover',
  secondary:
    'bg-surface text-primary border border-(--color-border-default) hoverable active:bg-subtle',
  ghost: 'bg-transparent text-accent-text active:bg-accent-subtle',
  danger: 'bg-danger text-on-accent hoverable active:bg-danger-hover',
};

const sizes: Record<Size, string> = {
  // `lg` is the customer-flow size: 56px tall, comfortably past the 48px floor
  // for a device held one-handed.
  lg: 'min-h-(--size-touch-comfortable) px-6 text-md rounded-md',
  md: 'min-h-(--size-control-md) px-4 text-sm rounded-md',
};

export function Button({
  variant = 'primary',
  size = 'lg',
  loading = false,
  fullWidth = false,
  disabled,
  children,
  className = '',
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <button
      {...rest}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={[
        'inline-flex items-center justify-center gap-2 font-medium',
        'transition-colors select-none',
        sizes[size],
        isDisabled
          ? 'bg-disabled text-disabled-text border-transparent cursor-not-allowed'
          : variants[variant],
        fullWidth ? 'w-full' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
